import { SettingsSection } from "./settings-section";
import { Badge } from "../../badge";

export interface KeyboardShortcut {
  /** Display label, e.g. "Open inbox". */
  label: string;
  /** Key combo as a list of tokens, e.g. ["g", "i"] or ["⌘", "K"]. */
  keys: string[];
  /** Optional descriptive context, e.g. "Anywhere". */
  context?: string;
}

export interface ShortcutGroup {
  label: string;
  shortcuts: KeyboardShortcut[];
}

export interface KeyboardShortcutsListProps {
  groups: ShortcutGroup[];
}

/**
 * A stack of shortcut groups, each rendered as a {@link SettingsSection} so the
 * group title matches every other section on a settings page (same weight,
 * same hairline divider, count pill for the shortcut tally) — not the old bare
 * `CardTitle` this used to draw, which read a step lighter than its neighbors.
 */
export function KeyboardShortcutsList({ groups }: KeyboardShortcutsListProps) {
  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <SettingsSection key={g.label} title={g.label} count={g.shortcuts.length}>
          {g.shortcuts.length === 0 ? (
            <p className="type-body text-muted-foreground">
              No shortcuts in this group.
            </p>
          ) : (
            <ul className="flex flex-col">
              {g.shortcuts.map((s, i) => (
                <li
                  key={`${s.label}-${i}`}
                  className="flex items-center justify-between gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0"
                >
                  <span className="type-body text-foreground">{s.label}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {s.context ? (
                      <span className="type-body text-muted-foreground/70 mr-2">
                        {s.context}
                      </span>
                    ) : null}
                    {s.keys.map((k, ki) => (
                      <Badge key={ki} variant="outline" className="font-mono">
                        {k}
                      </Badge>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SettingsSection>
      ))}
    </div>
  );
}
